import { trimDecimals } from '@einhaus/meteoric-helpers';
function calculateTradeProfitPercentageFromRiskCapital(trade) {
    if (trade.profit === null || trade.profit === undefined) {
        return null;
    }
    const openingTransactionRiskCapital = trade.transactions?.find((transaction) => transaction.isOpeningTrade && transaction.riskCapital && transaction.riskCapital > 0)
        ?.riskCapital ??
        trade.transactions?.find((transaction) => transaction.riskCapital && transaction.riskCapital > 0)?.riskCapital ??
        null;
    if (!openingTransactionRiskCapital || openingTransactionRiskCapital <= 0) {
        return null;
    }
    return (trade.profit / openingTransactionRiskCapital) * 100;
}
/**
 * Helper function to extract profit percentages from trades
 * Uses profitPercentage field when available, otherwise calculates from profit/riskCapital
 */
function extractProfitPercentages(trades) {
    if (!trades || trades.length === 0) {
        return {
            validProfitPercentages: [],
            totalWinPct: 0,
            totalLossPct: 0,
            profitFactor: 1,
            medianProfit: 0
        };
    }
    const validProfitPercentages = [];
    let totalWinPct = 0;
    let totalLossPct = 0;
    for (const trade of trades) {
        // The historical execution engine owns completed-trade economics. This helper only
        // derives a presentation fallback when an older row has no persisted percentage.
        let pctProfit = trade.profitPercentage;
        if (pctProfit === null || pctProfit === undefined) {
            pctProfit = calculateTradeProfitPercentageFromRiskCapital(trade);
        }
        if (pctProfit !== null && pctProfit !== undefined) {
            validProfitPercentages.push(pctProfit);
            if (pctProfit > 0) {
                totalWinPct += pctProfit;
            }
            else if (pctProfit < 0) {
                totalLossPct += Math.abs(pctProfit);
            }
        }
    }
    // Calculate profit factor from percentages
    const profitFactor = totalLossPct > 0 ? totalWinPct / totalLossPct : totalWinPct > 0 ? 5 : 1;
    // Calculate median from percentages
    let medianProfit = 0;
    if (validProfitPercentages.length > 0) {
        const sortedProfits = [...validProfitPercentages].sort((a, b) => a - b);
        const medianIndex = Math.floor(sortedProfits.length / 2);
        medianProfit =
            sortedProfits.length % 2 === 0
                ? ((sortedProfits[medianIndex - 1] ?? 0) + (sortedProfits[medianIndex] ?? 0)) / 2
                : (sortedProfits[medianIndex] ?? 0);
    }
    return {
        validProfitPercentages,
        totalWinPct,
        totalLossPct,
        profitFactor,
        medianProfit
    };
}
/**
 * Calculate a conservative risk-adjusted score for backtest ranking
 * Emphasizes consistency, risk management, and downside protection
 *
 * Score ranges typically 0-3:
 * - 0.0-0.5: Poor performance
 * - 0.5-1.0: Below average
 * - 1.0-1.5: Average
 * - 1.5-2.0: Good
 * - 2.0+: Excellent
 *
 * @param backtest - The backtest to score
 * @returns A composite score (higher is better)
 */
export function calculateRiskAdjustedScore(backtest) {
    const { avgTradeProfit, winRate, numTrades, maxDrawdown, trades, profit, id } = backtest;
    // DEBUG logging for specific backtests
    const DEBUG_IDS = [328692, 316958];
    const shouldDebug = DEBUG_IDS.includes(id);
    // Handle edge cases
    if (!trades || trades.length === 0) {
        return 0;
    }
    // Input validation
    const safeWinRate = Math.max(0, Math.min(100, winRate));
    // Extract profit percentages from trades
    const { profitFactor, medianProfit, validProfitPercentages } = extractProfitPercentages(trades);
    // Calculate risk-adjusted return properly
    // Use the actual average profit percentage from trades if available
    const avgProfitPct = validProfitPercentages.length > 0 ? validProfitPercentages.reduce((a, b) => a + b, 0) / validProfitPercentages.length : 0;
    const safeMaxDrawdown = Math.max(Math.abs(maxDrawdown), 1);
    // This gives us return per unit of drawdown risk
    const riskAdjustedReturn = avgProfitPct / safeMaxDrawdown;
    // Calculate profit magnitude with better differentiation
    // Use square root for better distribution than log
    // Normalize based on typical profit ranges: $0 to $500K
    const absoluteProfit = profit ?? 0;
    // Square root provides better differentiation for mid-range profits
    // sqrt(100K) = 316, sqrt(200K) = 447, sqrt(500K) = 707
    const profitMagnitude = absoluteProfit > 0
        ? Math.min(1, Math.sqrt(absoluteProfit) / 707) // sqrt(500K) = 707
        : 0;
    if (shouldDebug) {
        console.log(`\n=== RISK SCORE DEBUG for Backtest ${id} ===`);
        console.log('Input values:', {
            profit,
            avgTradeProfit,
            numTrades,
            maxDrawdown,
            winRate: safeWinRate,
            tradesLength: trades.length
        });
        console.log('Extracted values:', {
            profitFactor,
            medianProfit
        });
        console.log('Calculated values:', {
            avgProfitPct,
            riskAdjustedReturn,
            absoluteProfit,
            profitMagnitude
        });
    }
    // Normalize components with realistic ranges for PERCENTAGES
    // Profit factor: typical range 0.5-3.0
    const normalizedProfitFactor = Math.min(Math.max((profitFactor - 0.5) / 2.5, 0), 1);
    // Risk-adjusted return: now using percentage/drawdown ratio
    // Typical range: -0.5 to 2.0 (e.g., 2% avg profit with 1% drawdown = 2.0)
    const normalizedRiskAdjReturn = Math.min(Math.max((riskAdjustedReturn + 0.5) / 2.5, 0), 1);
    // Median profit PERCENTAGE: typical range -10% to +10%
    // For risk-adjusted score, apply FULL penalty for negative median
    const normalizedMedianProfit = Math.min(Math.max((medianProfit + 10) / 20, 0), 1);
    // Win rate: normalize to emphasize 45-55% sweet spot
    const normalizedWinRate = Math.min(Math.max((safeWinRate - 30) / 40, 0), 1);
    // Profit magnitude: already normalized 0-1 via log scale
    const normalizedProfitMagnitude = Math.min(profitMagnitude, 1);
    // Calculate confidence factor based on sample size
    const confidence = 0.7 + 0.3 * Math.min(numTrades / 200, 1);
    // RISK-ADJUSTED WEIGHTS: Emphasize consistency and risk management
    const rawScore = 0.3 * normalizedProfitFactor + // 30% weight on win/loss consistency
        0.3 * normalizedRiskAdjReturn + // 30% weight on risk-adjusted returns
        0.2 * normalizedMedianProfit + // 20% weight on typical trade outcome
        0.1 * normalizedWinRate + // 10% weight on win rate
        0.1 * normalizedProfitMagnitude; // 10% weight on absolute profit
    // Apply confidence and scale to 0-3 range
    const score = rawScore * confidence * 3;
    return trimDecimals(score, 4);
}
/**
 * Calculate an aggressive profit-weighted score for backtest ranking
 * Emphasizes total returns, profit potential, and growth
 *
 * Score ranges typically 0-3:
 * - 0.0-0.5: Poor performance
 * - 0.5-1.0: Below average
 * - 1.0-1.5: Average
 * - 1.5-2.0: Good
 * - 2.0+: Excellent
 *
 * @param backtest - The backtest to score
 * @returns A composite score (higher is better)
 */
export function calculateProfitWeightedScore(backtest) {
    const { avgTradeProfit, winRate, numTrades, maxDrawdown, trades, profit, id } = backtest;
    // DEBUG logging for specific backtests
    const DEBUG_IDS = [328692, 316958];
    const shouldDebug = DEBUG_IDS.includes(id);
    // Handle edge cases
    if (!trades || trades.length === 0) {
        return 0;
    }
    // Input validation
    const safeWinRate = Math.max(0, Math.min(100, winRate));
    // Extract profit percentages from trades
    const { profitFactor, medianProfit: rawMedianProfit, validProfitPercentages } = extractProfitPercentages(trades);
    // SMART ADJUSTMENT for profit-weighted score
    // Don't overly penalize profitable strategies with negative median
    const isProfitableOverall = (profit && profit > 0) || avgTradeProfit > 0;
    let medianProfit = rawMedianProfit;
    if (isProfitableOverall && rawMedianProfit < 0) {
        // For highly profitable strategies, ignore negative median entirely
        // This reflects that high-variance strategies can be extremely valuable
        if (profit && profit > 100000) {
            medianProfit = 0; // Neutral - don't penalize at all
        }
        else {
            // For moderately profitable, use only 25% of the negative value
            medianProfit = rawMedianProfit * 0.25;
        }
    }
    // Calculate risk-adjusted return properly
    // Use the actual average profit percentage from trades if available
    const avgProfitPct = validProfitPercentages.length > 0 ? validProfitPercentages.reduce((a, b) => a + b, 0) / validProfitPercentages.length : 0;
    const safeMaxDrawdown = Math.max(Math.abs(maxDrawdown), 1);
    // This gives us return per unit of drawdown risk
    const riskAdjustedReturn = avgProfitPct / safeMaxDrawdown;
    // Calculate profit magnitude with better differentiation
    // Use square root for better distribution than log
    // Normalize based on typical profit ranges: $0 to $500K
    const absoluteProfit = profit ?? 0;
    // Square root provides better differentiation for mid-range profits
    // sqrt(100K) = 316, sqrt(200K) = 447, sqrt(500K) = 707
    const profitMagnitude = absoluteProfit > 0
        ? Math.min(1, Math.sqrt(absoluteProfit) / 707) // sqrt(500K) = 707
        : 0;
    if (shouldDebug) {
        console.log(`\n=== PROFIT SCORE DEBUG for Backtest ${id} ===`);
        console.log('Input values:', {
            profit,
            avgTradeProfit,
            numTrades,
            maxDrawdown,
            winRate: safeWinRate,
            tradesLength: trades.length
        });
        console.log('Extracted values:', {
            profitFactor,
            rawMedianProfit,
            medianProfit,
            isProfitableOverall,
            medianDampened: isProfitableOverall && rawMedianProfit < 0
        });
        console.log('Calculated values:', {
            avgProfitPct,
            riskAdjustedReturn,
            absoluteProfit,
            profitMagnitude
        });
    }
    // Normalize components with realistic ranges
    // Profit factor: typical range 0.5-3.0
    const normalizedProfitFactor = Math.min(Math.max((profitFactor - 0.5) / 2.5, 0), 1);
    // Risk-adjusted return: now using percentage/drawdown ratio
    // Typical range: -0.5 to 2.0 (e.g., 2% avg profit with 1% drawdown = 2.0)
    const normalizedRiskAdjReturn = Math.min(Math.max((riskAdjustedReturn + 0.5) / 2.5, 0), 1);
    // Median profit with dampening applied
    const normalizedMedianProfit = Math.min(Math.max((medianProfit + 10) / 20, 0), 1);
    // Win rate: less important for profit-focused score
    const normalizedWinRate = Math.min(Math.max((safeWinRate - 30) / 40, 0), 1);
    // Profit magnitude: already normalized 0-1 via log scale
    const normalizedProfitMagnitude = Math.min(profitMagnitude, 1);
    if (shouldDebug) {
        console.log('Normalized components:', {
            normalizedProfitFactor,
            normalizedRiskAdjReturn,
            normalizedMedianProfit,
            normalizedWinRate,
            normalizedProfitMagnitude
        });
        console.log('Profit magnitude calculation:', {
            absoluteProfit,
            sqrtProfit: Math.sqrt(absoluteProfit),
            divisor: 707,
            result: Math.sqrt(absoluteProfit) / 707
        });
    }
    // Calculate confidence factor based on sample size
    const confidence = 0.7 + 0.3 * Math.min(numTrades / 200, 1);
    // PROFIT-WEIGHTED WEIGHTS: Emphasize returns and growth potential
    // For profit-weighted score, actual profit should dominate
    const isProfitSignificant = absoluteProfit > 50000;
    // Much heavier weight on actual profit for this score type
    const profitWeight = isProfitSignificant ? 0.5 : 0.35;
    const medianWeight = isProfitSignificant ? 0.05 : 0.1; // Reduce median impact
    const remainingWeight = 1 - profitWeight - medianWeight;
    const rawScore = profitWeight * normalizedProfitMagnitude + // 35-50% on absolute returns
        remainingWeight * 0.4 * normalizedProfitFactor + // ~18-20% on profit factor
        remainingWeight * 0.3 * normalizedRiskAdjReturn + // ~13-15% on efficiency
        medianWeight * normalizedMedianProfit + // 5-10% on median (dampened)
        remainingWeight * 0.3 * normalizedWinRate; // ~13-15% on win rate
    // Apply confidence and scale to 0-3 range
    const score = rawScore * confidence * 3;
    if (shouldDebug) {
        console.log('Score calculation:', {
            profitWeight,
            medianWeight,
            remainingWeight,
            rawScore,
            confidence,
            finalScore: score
        });
        console.log('Weight breakdown:', {
            profitContribution: profitWeight * normalizedProfitMagnitude,
            profitFactorContribution: remainingWeight * 0.45 * normalizedProfitFactor,
            riskAdjContribution: remainingWeight * 0.35 * normalizedRiskAdjReturn,
            medianContribution: medianWeight * normalizedMedianProfit,
            winRateContribution: remainingWeight * 0.2 * normalizedWinRate
        });
    }
    return trimDecimals(score, 4);
}
//# sourceMappingURL=backtestCalculations.js.map